/**
 * App-side analytics bootstrap.
 *
 * GTM (GTM-TGHVGDB2 → GA4) loads ONLY on the production host and ONLY after the
 * visitor opts into the "analytics" cookie category — mirroring the marketing
 * site (apps/website/src/components/CookieConsent.astro).
 *
 * On localhost / 127.0.0.1 / preview hosts we return early: no consent banner
 * and no GTM. This keeps dev servers and Playwright e2e from sending hits to
 * GA4 (they were previously ~77% of the property's "active users") and keeps
 * the banner from ever appearing during tests.
 */
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import { isAppAnalyticsHost } from './analytics-host';

const GTM_ID = 'GTM-TGHVGDB2';

let gtmLoaded = false;

/** Inject the GTM loader (idempotent). Same snippet that used to live in index.html. */
function loadGtm(): void {
  if (gtmLoaded) return;
  gtmLoaded = true;

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const first = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  first.parentNode?.insertBefore(script, first);
}

/** Load GTM once the analytics category is accepted. */
function syncGtmWithConsent(): void {
  if (CookieConsent.acceptedCategory('analytics')) loadGtm();
}

export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!isAppAnalyticsHost(window.location.hostname)) return;

  CookieConsent.run({
    mode: 'opt-in',

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {},
    },

    guiOptions: {
      consentModal: {
        layout: 'bar inline',
        position: 'bottom',
      },
    },

    onConsent: syncGtmWithConsent,
    onChange: syncGtmWithConsent,

    language: {
      default: 'fr',
      autoDetect: 'document',
      translations: {
        fr: {
          consentModal: {
            title: 'Nous utilisons des cookies',
            description:
              "Ce site utilise des cookies analytiques pour comprendre comment vous l'utilisez et améliorer votre expérience. <a href='https://bill-alps.ch/fr/privacy'>Politique de confidentialité</a>",
            acceptAllBtn: 'Accepter',
            acceptNecessaryBtn: 'Refuser',
          },
          preferencesModal: {
            title: 'Préférences de cookies',
            acceptAllBtn: 'Accepter',
            acceptNecessaryBtn: 'Refuser',
            savePreferencesBtn: 'Enregistrer les préférences',
            sections: [],
          },
        },
        de: {
          consentModal: {
            title: 'Wir verwenden Cookies',
            description:
              "Diese Website verwendet Analyse-Cookies, um zu verstehen, wie Sie sie nutzen, und um Ihre Erfahrung zu verbessern. <a href='https://bill-alps.ch/de/privacy'>Datenschutzerklärung</a>",
            acceptAllBtn: 'Akzeptieren',
            acceptNecessaryBtn: 'Ablehnen',
          },
          preferencesModal: {
            title: 'Cookie-Einstellungen',
            acceptAllBtn: 'Akzeptieren',
            acceptNecessaryBtn: 'Ablehnen',
            savePreferencesBtn: 'Einstellungen speichern',
            sections: [],
          },
        },
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              "This site uses analytics cookies to understand how you use it and improve your experience. <a href='https://bill-alps.ch/en/privacy'>Privacy policy</a>",
            acceptAllBtn: 'Accept',
            acceptNecessaryBtn: 'Reject',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept',
            acceptNecessaryBtn: 'Reject',
            savePreferencesBtn: 'Save preferences',
            sections: [],
          },
        },
      },
    },
  });
}
